import { useEffect, useReducer } from 'react'
import { subtract, vn } from 'vend-number'
import { isGiftCard } from '../../functions/lineItem'
import { LineItem, UnknownLineItem } from '../../models/line-item'
import { useSaleInventoryData } from '../../ng-compat/hooks'
import { getFeatureFromCache } from '../../util/feature-cache'
import { SaleInventoryData } from '../../components/sale/sale-inventory-data/sale-inventory-data.service'
import { selectSaleLocalId } from '../../state/slices/currentSale/currentSaleSlice'
import { useAppSelector } from '../../state/hooks'
import { useCrossLocationFulfilmentGate } from '../sale/sale-line-item/fulfilment/OutletSelect'
import {
  getAvailabilityByOutletId,
  getCachedAvailability,
} from '../../util/cross-location-fulfilment'

/**
 * This hook is to determine if a line item in a sale is a special order product
 */
export const useSpecialOrderProduct = (currentLineItem?: LineItem) => {
  const saleInventoryData: SaleInventoryData = useSaleInventoryData()
  const saleLocalId = useAppSelector(selectSaleLocalId)
  const { canUseCrossLocationFulfilment } = useCrossLocationFulfilmentGate()
  const forceUpdate = useReducer(version => version + 1, 0)[1]

  useEffect(() => {
    if (
      !canUseCrossLocationFulfilment ||
      !currentLineItem?.fulfilmentOutletId ||
      !currentLineItem.product.has_inventory ||
      !saleLocalId
    ) {
      return
    }

    let isStale = false

    getAvailabilityByOutletId(
      currentLineItem.product.id,
      [currentLineItem.fulfilmentOutletId],
      saleLocalId
    ).then(() => {
      if (!isStale) {
        forceUpdate()
      }
    })

    return () => {
      isStale = true
    }
  }, [
    canUseCrossLocationFulfilment,
    currentLineItem?.fulfilmentOutletId,
    currentLineItem?.product.has_inventory,
    currentLineItem?.product.id,
    saleLocalId,
    // React guarantees that the dispatch function returned by useReducer has a stable identity,
    // so forceUpdate does not need to be in the dependency array, eslint doesn't like it though
    forceUpdate,
  ])

  const hasCrossLocationInventory = (lineItem: LineItem) => {
    if (!lineItem.fulfilmentOutletId) {
      return undefined
    }

    const availability = getCachedAvailability(
      lineItem.product.id,
      lineItem.fulfilmentOutletId,
      saleLocalId
    )

    if (availability === undefined) {
      return undefined
    }

    // A persisted line item's quantity is already reserved at its outlet and
    // deducted from the fetched availability; its type, outlet and quantity
    // are locked on a continued fulfilment sale, so the reservation always
    // equals the current quantity and the item's own demand is netted out.
    const effectiveAvailability = lineItem.id
      ? availability
      : subtract(availability, lineItem.quantity || 0)

    return !vn(effectiveAvailability).lt(0)
  }

  const isSpecialOrderProduct = (
    lineItem: LineItem | UnknownLineItem,
    skipFFCheck = false
  ) => {
    if (!skipFFCheck) {
      const ffEnabled = getFeatureFromCache('inventory_special_orders')
      // ff disabled
      if (!ffEnabled) {
        return false
      }
    }

    // unknown line item
    if (!('fulfilmentType' in lineItem)) {
      return false
    }

    // returned line item
    if (lineItem.isReturn) {
      return false
    }

    // fulfilmentType not pickup or dispatch
    if (
      lineItem.fulfilmentType !== 'PICKUP' &&
      lineItem.fulfilmentType !== 'DISPATCH'
    ) {
      return false
    }

    // gift card
    if (isGiftCard(lineItem.product)) {
      return false
    }

    // quantity <= 0
    const quantity = vn(lineItem.quantity || 0)
    if (quantity.lte(0)) {
      return false
    }

    // composite product
    if (lineItem.product.is_composite) {
      return false
    }

    if (
      canUseCrossLocationFulfilment &&
      lineItem.product.has_inventory &&
      lineItem.fulfilmentOutletId
    ) {
      const hasInventory = hasCrossLocationInventory(lineItem)

      if (hasInventory !== undefined) {
        return !hasInventory
      }
    }

    // non-inventory item
    if (!saleInventoryData.getInventoryData(lineItem)) {
      return false
    }

    // item in stock
    /**
     * use hasInventory to check for product in stock
     * hasInventory checks the inventory level against the cumulative quantity of all line items
     * if an item has enough inventory, all split line items are considered in stock (regardless of how they are split)
     * if an item has not enough inventory, all split line items are considered out of stock (regardless of how they are split)
     */
    if (saleInventoryData.hasInventory(lineItem)) {
      return false
    }

    // out of stock product
    return true
  }

  return isSpecialOrderProduct
}
